/**
=========================================================
* ooGoal - v5.0.0
=========================================================

* Product Page: https://oogoal.com
* Copyright 2022 Onza Technologies

=========================================================

* The above copyright must be included
*/

import { createContext, useContext, useReducer, useMemo } from "react";
import PropTypes from "prop-types";

const ThemeUI = createContext();

ThemeUI.displayName = "ThemeUIContext";

// React reducer
function reducer(state, action) {
  switch (action.type) {
    case "COLLAPSE_SIDENAV": {
      return { ...state, collapseSidenav: action.value };
    }
    case "SIDENAV_COLOR": {
      return { ...state, sidenavColor: action.value };
    }
    case "FIXED_NAVBAR": {
      return { ...state, fixedNavbar: action.value };
    }
    case "OPEN_CONFIGURATOR": {
      return { ...state, openConfigurator: action.value };
    }
    case "LAYOUT": {
      return { ...state, layout: action.value };
    }
    case "DARKMODE": {
      return { ...state, darkMode: action.value };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

// Material Dashboard 2 React context provider
function ThemeUIControllerProvider({ children }) {
  const initialState = {
    collapseSidenav: false,
    transparentNavbar: true,
    fixedNavbar: true,
    openConfigurator: false,
    layout: "dashboard",
    darkMode: false,
  };

  const [controller, dispatch] = useReducer(reducer, initialState);

  const value = useMemo(() => [controller, dispatch], [controller, dispatch]);

  return <ThemeUI.Provider value={value}>{children}</ThemeUI.Provider>;
}

// Material Dashboard 2 React custom hook for using context
function useThemeUIController() {
  const context = useContext(ThemeUI);

  if (!context) {
    throw new Error("useThemeUIController should be used inside the ThemeUIControllerProvider.");
  }

  return context;
}

ThemeUIControllerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Context module functions
const setCollapseSidenav = (dispatch, value) => dispatch({ type: "COLLAPSE_SIDENAV", value });
const setFixedNavbar = (dispatch, value) => dispatch({ type: "FIXED_NAVBAR", value });
const setOpenConfigurator = (dispatch, value) => dispatch({ type: "OPEN_CONFIGURATOR", value });
const setLayout = (dispatch, value) => dispatch({ type: "LAYOUT", value });
const setDarkMode = (dispatch, value) => dispatch({ type: "DARKMODE", value });

export {
  ThemeUIControllerProvider,
  useThemeUIController,
  setCollapseSidenav,
  setFixedNavbar,
  setOpenConfigurator,
  setLayout,
  setDarkMode,
};
